import ExcelJS from "exceljs";
import { ACCEPTANCE_ITEM_IMPORT_COLUMNS } from "./validateAcceptanceItemsImport";

export const ACCEPTANCE_IMPORT_IGNORED_SHEETS = [
  "Tổng hợp",
  "Tong hop",
  "Danh_muc_co_dinh",
  "Phien_ban",
];

// Chuẩn hóa tiêu đề/sheet để so khớp không phụ thuộc dấu và khoảng trắng.
const normalizeLookup = (value) =>
  (value || "")
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/[_\s]+/g, " ");

// Lấy giá trị thực của ô Excel, bao gồm công thức, hyperlink và rich text.
const getCellValue = (cell) => {
  const value = cell?.value;
  if (value && typeof value === "object") {
    if (value.result !== undefined) return value.result;
    if (value.text !== undefined) return value.text;
    if (value.hyperlink && value.text) return value.text;
    if (Array.isArray(value.richText)) {
      return value.richText.map((part) => part.text).join("");
    }
  }
  return value;
};

// Bỏ qua các sheet chỉ phục vụ tổng hợp, danh mục cố định hoặc phiên bản file.
export const isIgnoredAcceptanceSheet = (sheetName) => {
  const normalizedName = normalizeLookup(sheetName);
  return ACCEPTANCE_IMPORT_IGNORED_SHEETS.some(
    (ignoredName) => normalizeLookup(ignoredName) === normalizedName,
  );
};

// Tìm dòng tiêu đề của một sheet dựa trên các cột bắt buộc trong mẫu import.
const findHeaderRow = (worksheet, expectedHeaders) => {
  let headerRow = null;
  const columnIndexByKey = {};

  worksheet.eachRow((row, rowNumber) => {
    if (headerRow) return;

    const normalizedCells = [];
    row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
      normalizedCells[colNumber] = normalizeLookup(getCellValue(cell));
    });

    const hasImportCode = expectedHeaders
      .find((column) => column.key === "importCode")
      ?.normalizedHeaders.some((header) => normalizedCells.includes(header));
    const hasItemName = expectedHeaders
      .find((column) => column.key === "itemName")
      ?.normalizedHeaders.some((header) => normalizedCells.includes(header));
    const hasReportedQuantity = expectedHeaders
      .find((column) => column.key === "reportedQuantity")
      ?.normalizedHeaders.some((header) => normalizedCells.includes(header));
    const hasAcceptedQuantity = expectedHeaders
      .find((column) => column.key === "acceptedQuantity")
      ?.normalizedHeaders.some((header) => normalizedCells.includes(header));

    if (!hasImportCode || !hasItemName || !hasReportedQuantity || !hasAcceptedQuantity) return;

    headerRow = rowNumber;
    expectedHeaders.forEach((column) => {
      const index = normalizedCells.findIndex((value) =>
        column.normalizedHeaders.includes(value),
      );
      if (index > -1) columnIndexByKey[column.key] = index;
    });
  });

  return { headerRow, columnIndexByKey };
};

// Đọc từng sheet chi tiết nghiệm thu thành danh sách dòng import có kèm tên sheet.
const readWorksheetRows = (worksheet, expectedHeaders) => {
  const { headerRow, columnIndexByKey } = findHeaderRow(worksheet, expectedHeaders);
  if (!headerRow) {
    return {
      rows: [],
      error: `Sheet "${worksheet.name}" không tìm thấy dòng tiêu đề đúng mẫu import nghiệm thu`,
    };
  }

  const rows = [];
  for (let rowNumber = headerRow + 1; rowNumber <= worksheet.rowCount; rowNumber += 1) {
    const worksheetRow = worksheet.getRow(rowNumber);
    const rowData = {
      sheetName: worksheet.name,
      rowNumber,
    };

    ACCEPTANCE_ITEM_IMPORT_COLUMNS.forEach((column) => {
      const colNumber = columnIndexByKey[column.key];
      rowData[column.key] = colNumber
        ? getCellValue(worksheetRow.getCell(colNumber))
        : "";
    });

    const hasValue = ACCEPTANCE_ITEM_IMPORT_COLUMNS.some((column) => {
      const value = rowData[column.key];
      return value !== null && value !== undefined && value.toString().trim() !== "";
    });
    if (hasValue) rows.push(rowData);
  }

  return { rows, error: null };
};

// Đọc file Excel import chi tiết nghiệm thu, mỗi sheet hợp lệ tương ứng một scope.
export const readAcceptanceItemsImportExcel = async (file) => {
  const workbook = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  await workbook.xlsx.load(buffer);

  if (!workbook.worksheets.length) {
    throw new Error("File Excel không có worksheet hợp lệ");
  }

  const expectedHeaders = ACCEPTANCE_ITEM_IMPORT_COLUMNS.map((column) => ({
    ...column,
    normalizedHeaders: [column.header, ...(column.aliases || [])].map(normalizeLookup),
  }));

  const sheets = [];
  const ignoredSheets = [];
  const errors = [];
  const rows = [];

  workbook.worksheets.forEach((worksheet) => {
    if (isIgnoredAcceptanceSheet(worksheet.name)) {
      ignoredSheets.push(worksheet.name);
      return;
    }

    sheets.push(worksheet.name);
    const result = readWorksheetRows(worksheet, expectedHeaders);
    rows.push(...result.rows);
    if (result.error) errors.push({ sheetName: worksheet.name, message: result.error });
  });

  if (!sheets.length) {
    throw new Error("File Excel không có sheet chi tiết nghiệm thu hợp lệ");
  }

  return {
    rows,
    sheets,
    ignoredSheets,
    errors,
  };
};

export default readAcceptanceItemsImportExcel;
